// src/config/supabaseImages.js
// Configuración avanzada para las URLs de imágenes de Supabase
import React from 'react';
import { supabase } from '../Supabase/SupabaseClient'; // Ajusta la ruta según tu estructura

// Build seguro de la URL base del bucket público (si está definida)
const REACT_SUPABASE = process.env.REACT_APP_SUPABASE_URL || '';
if (!REACT_SUPABASE) {
  console.warn('[WARN] REACT_APP_SUPABASE_URL no está definido. Las rutas de imagen quedarán vacías.');
}
const SUPABASE_URL = REACT_SUPABASE
  ? `${REACT_SUPABASE}/storage/v1/object/public/Assets/Imagenes`
  : '';

// Helper para crear rutas seguras (devuelve cadena vacía si no hay URL)
const assetUrl = (fileName) => (SUPABASE_URL ? `${SUPABASE_URL}/${fileName}` : '');

// Objeto con todas las rutas de imágenes (usa assetUrl para evitar "undefined" en la URL)
export const IMAGES = {
  // Imágenes del fotógrafo (URL externa, no vive en el bucket de Supabase)
  photographer: 'https://lizpinto.net/wp-content/uploads/2025/02/Sesiones-de-15-anos-en-Panama_Liz-Pinto.jpg',

  // Imágenes de inspiración
  inspiration: {
    img1: assetUrl('IMG_2422.JPG'),
    img2: assetUrl('DSC_3573-Editar-Editar.JPG'),
    img3: assetUrl('DSC_8718-Editar.jpg'),
    img4: assetUrl('DSC_9632-Editar.jpg'),
  },

  // Imágenes de frases
  frases: {
    carting: assetUrl('carting.webp'),
    humo: assetUrl('humo.webp'),
    mano: assetUrl('mano.webp'),
    pelado: assetUrl('pelado.webp'),
  },

  // Imágenes del carrusel
  carousel: {
    guitarra: assetUrl('guitarra.webp'),
    trece: assetUrl('trece.webp'),
    jp: assetUrl('jp.webp'),
    rosa: assetUrl('rosa.webp'),
    carting: assetUrl('carting.webp'),
    ramo: assetUrl('ramo.webp'),
    chicaCaballo: assetUrl('chica_caballo.webp'),
    boina: assetUrl('boina.webp'),
    flia: assetUrl('flia.webp'),
    faro: assetUrl('faro.webp'),
    montana: assetUrl('montana.webp'),
    caballo: assetUrl('caballo.webp'),
    silla: assetUrl('silla.webp'),
    reja: assetUrl('reja.webp'),
    lu: assetUrl('lu.webp'),
  },

  // Imágenes de home
  home: {
    img1: assetUrl('DSC_4002-Mejorado-NR-Editar.JPG'),
    img2: assetUrl('DSC_0885.JPG'),
    img3: assetUrl('DSC_7675-Editar.JPG'),
  },

  // Galería polaroid de la home (usada por PolaroidGallery.jsx)
  gallery: {
    avioneta: assetUrl('avioneta.webp'),
    nieve: assetUrl('nieve.webp'),
    auriculares: assetUrl('auriculares.webp'),
  },

  // Imágenes de secciones específicas
  boda: assetUrl('boda.webp'),
  boudoir: assetUrl('guitarra.webp'),
  marcaPersonal: assetUrl('IMG_2471-Editar.JPG'),

  // Logos
  logos: {
    logoNegro: assetUrl('SoleCostillaGroup_logotipo_NEGRO.png'),
    logoBlanco: assetUrl('SoleCostillaGroup_sintesis_BLANCO.png'),
    logoNegroSintesis: assetUrl('SoleCostillaGroup_sintesis_NEGRO.png'),
  },
};

// Función para obtener URL pública de Supabase Storage (segura)
export const getPublicUrl = (bucketName, filePath) => {
  try {
    const { data, error } = supabase.storage.from(bucketName).getPublicUrl(filePath);
    if (error) {
      console.warn('getPublicUrl error:', error);
      return null;
    }
    return data?.publicUrl ?? null;
  } catch (err) {
    console.error('getPublicUrl caught:', err);
    return null;
  }
};

// Función para obtener URL de imagen específica desde el bucket Assets
export const getAssetImageUrl = (imageName) => {
  return getPublicUrl('Assets', `Imagenes/${imageName}`);
};

// Función helper para obtener URL de imagen con fallback
export const getImageUrl = (imagePath, fallback = null) => {
  if (!imagePath) return fallback;
  return imagePath;
};

// Función para precargar imágenes críticas
export const preloadImages = (imageUrls) => {
  if (typeof document === 'undefined') return;
  imageUrls.forEach((url) => {
    if (!url) return;
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = url;
    document.head.appendChild(link);
  });
};

// Función para verificar si una imagen existe (HEAD). Devuelve false si URL vacía o error.
export const checkImageExists = async (imageUrl) => {
  if (!imageUrl) return false;
  try {
    const response = await fetch(imageUrl, { method: 'HEAD' });
    return response.ok;
  } catch (error) {
    // Nota: algunos buckets pueden bloquear HEAD por CORS; handle gracefully
    console.warn('Error checking image (HEAD):', error);
    return false;
  }
};

// Hook personalizado para cargar imágenes dinámicamente
export const useSupabaseImage = (bucketName, imagePath) => {
  const [imageUrl, setImageUrl] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    let mounted = true;
    const loadImage = async () => {
      setLoading(true);
      setError(null);
      try {
        const url = getPublicUrl(bucketName, imagePath);
        if (!url) {
          throw new Error('Public URL not available');
        }
        const exists = await checkImageExists(url);
        if (!mounted) return;
        if (exists) {
          setImageUrl(url);
        } else {
          setError('Image not found');
        }
      } catch (err) {
        if (!mounted) return;
        setError(err.message || String(err));
      } finally {
        if (!mounted) return;
        setLoading(false);
      }
    };

    if (bucketName && imagePath) {
      loadImage();
    } else {
      setLoading(false);
      setImageUrl(null);
    }

    return () => { mounted = false; };
  }, [bucketName, imagePath]);

  return { imageUrl, loading, error };
};

// Función para subir una nueva imagen
export const uploadImage = async (bucketName, file, filePath) => {
  try {
    const { data, error } = await supabase.storage
      .from(bucketName)
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false,
      });

    if (error) throw error;

    return {
      success: true,
      data,
      publicUrl: getPublicUrl(bucketName, filePath),
    };
  } catch (error) {
    console.error('Error uploading image:', error);
    return {
      success: false,
      error: error.message || String(error),
    };
  }
};

// Configuración de imágenes críticas para precargar (filtra vacíos)
// Solo lo que la primer pantalla usa seguro: `home.img1` y `photographer` se
// precargaban pero nunca se renderizan, y el warning "preloaded but not used"
// ensuciaba la consola en todas las rutas.
export const CRITICAL_IMAGES = [
  IMAGES.logos.logoNegro,
].filter(Boolean);

// Precargar imágenes críticas automáticamente (solo en cliente)
if (typeof window !== 'undefined') {
  preloadImages(CRITICAL_IMAGES);
}

export default IMAGES;
